import { useRouter } from 'next/router';
import { ClaimFlowType } from 'types';

import { PollingResult } from 'components/SubPages/ClaimInfo/pollTransactionStatus';
import useCreateClaimInfoFormHook from 'components/SubPages/ClaimInfo/useCreateClaimInfoFormHook';

import { MachineContext, useMachineContext } from 'hooks/useMachineContext';
import useTransactionType from 'hooks/useTransactionType';
import { useUserContext } from 'hooks/useUserContext';

import {
  InputFieldTrackingFunction,
  trackFieldFilled,
  trackPayAnyoneTermsAccepted,
  trackReceiveFromAnyoneTermsAccepted,
} from 'utils/segment/trackingEvents';

import MoneyRequestStatus from '../../../types/MoneyRequestStatus';
import PayAnyoneStatus from '../../../types/PayAnyoneStatus';

type UseClaimInfoBaseFormApi = {
  submitForm: (any) => Promise<void>;
  hasError: boolean;
  errorMessage?: string;
  errorId?: string;
  isLoading: boolean;
  dismissError: () => void;
  trackCardNumber: InputFieldTrackingFunction;
  trackCVV: InputFieldTrackingFunction;
  trackExpDate: InputFieldTrackingFunction;
  trackZipCode: InputFieldTrackingFunction;
  trackFirstName: InputFieldTrackingFunction;
  trackLastName: InputFieldTrackingFunction;
  trackEmail: InputFieldTrackingFunction;
  trackOnOcrButtonClick: () => void;
  trackOnSuccessfulOcrScan: (isPartialScan: boolean) => void;
  trackCardNumberOcr: (cardNumber: string) => void;
};

function useClaimInfoBaseForm(
  setChallengeContext?: (challengeContext: string) => void
): UseClaimInfoBaseFormApi {
  const [, send] = useMachineContext(MachineContext);
  const { claimType } = useTransactionType();
  const [userData] = useUserContext();
  const { publicId } = userData;
  const router = useRouter();
  const token = router.query.token;

  const postUrl =
    claimType === ClaimFlowType.pay
      ? `${process.env.CONSUMER_HOST}/pf/claim/${router.query.token}`
      : `${process.env.CONSUMER_HOST}/mr/pay/${router.query.token}`;

  const pollingUrl =
    claimType === ClaimFlowType.pay
      ? `${process.env.CONSUMER_HOST}/pf/${token}.json`
      : `${process.env.CONSUMER_HOST}/mr/${token}.json?show_step_up=true`;

  const pollingFunction =
    claimType === ClaimFlowType.pay
      ? getPayAnyonePollingStatus
      : getReceiveFromAnyonePollingStatus;

  const onFormSubmitSuccess = data => {
    const pageLocation = `${claimType} Claim Info Page`;
    if (claimType === ClaimFlowType.pay) {
      trackPayAnyoneTermsAccepted(data.terms, pageLocation, publicId);

      trackFieldFilled(
        claimType,
        'Pay Anyone Save Debit Card',
        pageLocation,
        data.cardSave,
        publicId
      );
    }

    if (claimType === ClaimFlowType.receive) {
      trackReceiveFromAnyoneTermsAccepted(data.terms, pageLocation, publicId);
    }

    send('NEXT');
  };

  const {
    submitForm,
    hasError,
    errorMessage,
    error,
    errorId,
    isLoading,
    dismissError,
    trackCardNumber,
    trackCVV,
    trackExpDate,
    trackZipCode,
    trackFirstName,
    trackLastName,
    trackEmail,
    trackOnOcrButtonClick,
    trackOnSuccessfulOcrScan,
    trackCardNumberOcr,
  } = useCreateClaimInfoFormHook(
    postUrl,
    claimType,
    onFormSubmitSuccess,
    pollingUrl,
    pollingFunction,
    setChallengeContext
  );

  if (hasError && !errorMessage) {
    if (!error) {
      send('ERROR_PAGE');
    } else {
      send('ERROR_PAGE', error as never);
    }
  }

  return {
    submitForm,
    hasError,
    errorMessage,
    errorId,
    isLoading,
    dismissError,
    trackCardNumber,
    trackCVV,
    trackExpDate,
    trackZipCode,
    trackFirstName,
    trackLastName,
    trackEmail,
    trackOnOcrButtonClick,
    trackOnSuccessfulOcrScan,
    trackCardNumberOcr,
  };
}

function getPayAnyonePollingStatus(result: any): PollingResult {
  return result?.status;
}

function getReceiveFromAnyonePollingStatus(result: any): PollingResult {
  const moneyRequestStatus = result?.status;
  const payAnyoneStatus = result?.payfriendStatus;

  if (moneyRequestStatus === MoneyRequestStatus.pending && payAnyoneStatus) {
    switch (payAnyoneStatus) {
      case PayAnyoneStatus.succeeded:
        return PollingResult.succeeded;
      case PayAnyoneStatus.pending:
        return PollingResult.pending;
      case PayAnyoneStatus.sent:
        return PollingResult.sent;
      case PayAnyoneStatus.failed:
        return PollingResult.failed;
      case PayAnyoneStatus.step_up_required:
        return PollingResult.step_up_required;
      default:
        return null;
    }
  }

  switch (moneyRequestStatus) {
    case MoneyRequestStatus.accepted:
      return PollingResult.succeeded;
    case MoneyRequestStatus.cancelled:
    case MoneyRequestStatus.expired:
      return PollingResult.expired;
    case MoneyRequestStatus.pending:
      return PollingResult.pending;
    default:
      return null;
  }
}

export default useClaimInfoBaseForm;
